import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getPaginatedSubCat } from "../api/ApiRoutesFile";

export const subCategoryApiSlice = createApi({
  reducerPath: "subCategoryApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["SubCategories"],
  endpoints: (builder) => ({
    subCategories: builder.query({
      query: ({ endpoint, id, params }) => {
        const url = id ? `${endpoint}/${id}` : endpoint;
        return {
          url,
          method: "GET",
          params: params || undefined,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result?.subCategories?.map(({ _id }) => ({
                type: "SubCategories",
                id: _id,
              })),
              { type: "SubCategories", id: "LIST" },
            ]
          : [{ type: "SubCategories", id: "LIST" }],
    }),

    createSubCategory: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Wait for the API response before updating the cache
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the real API response
          if (data && data.subCategory && data.subCategory._id) {
            // Update the paginated query cache (if using a page-based cache)
            dispatch(
              subCategoryApiSlice.util.updateQueryData(
                "subCategories",
                { endpoint: getPaginatedSubCat, id: lastId },
                (draft) => {
                  // Insert the new lot at the beginning (top)
                  draft.subCategories.unshift(data.subCategory);
                }
              )
            );
          }
        } catch (error) {
          console.error("Create failed:", error);
        }
      },
    }),

    updateSubCategory: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Wait for API response before updating the cache
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the API call to complete
          if (data && data.subCategory) {
            // Update paginated cache if applicable
            dispatch(
              subCategoryApiSlice.util.updateQueryData(
                "subCategories",
                { endpoint: getPaginatedSubCat, id: lastId },
                (draft) => {
                  const index = draft.subCategories.findIndex(
                    (a) => a._id === id
                  );
                  if (index !== -1) {
                    draft.subCategories[index] = data.subCategory;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),

    deleteSubCategory: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT", // or "DELETE" if your backend supports it
      }),

      // Update the cache only after a successful API response
      async onQueryStarted({ endpoint, id }, { dispatch, queryFulfilled }) {
        try {
          // Wait for API confirmation
          const { data } = await queryFulfilled;

          if (data && data?.subCategory) {
            dispatch(
              subCategoryApiSlice.util.updateQueryData(
                "subCategories",
                { endpoint: getPaginatedSubCat, id: 1 }, // Replace `1` with dynamic `lastId` if needed
                (draft) => {
                  if (draft && draft.subCategories) {
                    draft.subCategories = draft.subCategories.filter(
                      (item) => item._id !== id
                    );
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Delete subcategory failed:", error);
        }
      },
    }),
  }),
});

export const {
  useSubCategoriesQuery,
  useCreateSubCategoryMutation,
  useUpdateSubCategoryMutation,
  useDeleteSubCategoryMutation,
} = subCategoryApiSlice;
